import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import AnonymousWebLayout from './layouts/AnonymousWebLayout.jsx';
import { UnauthorizedPage, ForbiddenPage, NotFoundPage, ServerErrorPage } from './pages/status/index.jsx';
import LoginPage from './pages/login/index.jsx';
import RegisterPage from './pages/registration/index.jsx';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/forgot-password/index.jsx';
import { TermsPage, PrivacyPage } from './pages/legal/index.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import { ACCESS } from './layouts/menuData.js';
import UserRoleManagementLayout from './layouts/UserRoleManagementLayout.jsx';
import UserRoleManagementPage from './pages/user-role-management/index.jsx';
import UserProfileVehicleSettingsLayout from './layouts/UserProfileVehicleSettingsLayout.jsx';
import ProfilePage from './pages/user-profile-vehicle-settings/index.jsx';
import ChargerModelsCompatibilityPage from './pages/charger-models-compatibility/index.jsx';
import TariffConfigurationPage from './pages/tariff-configuration/index.jsx';
import StationLocatorAvailabilityLayout from './layouts/StationLocatorAvailabilityLayout.jsx';
import StationLocatorPage from './pages/station-locator-availability/index.jsx';
import WalletTopUpPaymentLayout from './layouts/WalletTopUpPaymentLayout.jsx';
import WalletTopUpPaymentPage from './pages/wallet-top-up-payment/index.jsx';
import LiveChargingTelemetryLayout from './layouts/LiveChargingTelemetryLayout.jsx';
import LiveChargingTelemetryPage from './pages/live-charging-telemetry/index.jsx';
import ConnectorReservationPage from './pages/connector-reservation/index.jsx';
import GuestCheckoutSessionLayout from './layouts/GuestCheckoutSessionLayout.jsx';
import GuestCheckoutSessionPage from './pages/guest-checkout-session/index.jsx';
import NocFleetMonitoringAlarmsLayout from './layouts/NocFleetMonitoringAlarmsLayout.jsx';
import NocFleetMonitoringAlarmsPage from './pages/noc-fleet-monitoring-alarms/index.jsx';
import ChargerRemoteControlsPage from './pages/charger-remote-controls/index.jsx';
import ChargerOnboardingPage from './pages/charger-onboarding/index.jsx';
import MaintenanceTicketsPage from './pages/maintenance-tickets-diagnostics/index.jsx';
import DriverDirectoryPage from './pages/driver-directory-mini-crm/index.jsx';
import CustomerDisputePage from './pages/customer-dispute-adjustment-console/index.jsx';
import FinancialReconciliationLedgerPage from './pages/financial-reconciliation-ledger/index.jsx';
import ExecutiveDashboardPage from './pages/executive-management-dashboard/index.jsx';
import ExecutiveManagementDashboardLayout from './layouts/ExecutiveManagementDashboardLayout.jsx';
import './App.scss';

// Pages and layouts are generated per the design reference. Lazy-import them and
// wire their routes here, e.g.:
//
//   import { lazy } from 'react';
//   import { Route } from 'react-router-dom';
//   import ProtectedRoute from './components/ProtectedRoute.jsx';
//   const HomePage = lazy(() => import('./pages/HomePage.jsx'));
//
//   <Routes>
//     <Route path="/" element={<HomePage />} />
//     <Route element={<ProtectedRoute />}>
//       <Route path="/dashboard" element={<DashboardPage />} />
//     </Route>
//   </Routes>

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Routes go here */}
            <Route element={<ProtectedRoute roles={ACCESS.admin} />}>
              <Route element={<UserRoleManagementLayout />}>
                <Route path="/user-role-management" element={<UserRoleManagementPage />} />
                <Route path="/charger-models-compatibility" element={<ChargerModelsCompatibilityPage />} />
                <Route path="/tariff-configuration" element={<TariffConfigurationPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<UserProfileVehicleSettingsLayout />}>
                <Route path="/user-profile-vehicle-settings" element={<ProfilePage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.driver} />}>
              <Route element={<WalletTopUpPaymentLayout />}>
                <Route path="/wallet-top-up-payment" element={<WalletTopUpPaymentPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.driver} />}>
              <Route element={<WalletTopUpPaymentLayout />}>
                <Route path="/connector-reservation" element={<ConnectorReservationPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.driver} />}>
              <Route element={<LiveChargingTelemetryLayout />}>
                <Route path="/live-charging-telemetry" element={<LiveChargingTelemetryPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.noc} />}>
              <Route element={<NocFleetMonitoringAlarmsLayout />}>
                <Route path="/noc-fleet-monitoring-alarms" element={<NocFleetMonitoringAlarmsPage />} />
                <Route path="/charger-remote-controls" element={<ChargerRemoteControlsPage />} />
                <Route path="/charger-onboarding" element={<ChargerOnboardingPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.customerCare} />}>
              <Route element={<UserRoleManagementLayout />}>
                <Route path="/driver-directory-mini-crm" element={<DriverDirectoryPage />} />
                <Route path="/customer-dispute-adjustment-console" element={<CustomerDisputePage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.finance} />}>
              <Route element={<UserRoleManagementLayout />}>
                <Route path="/financial-reconciliation-ledger" element={<FinancialReconciliationLedgerPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.finance} />}>
              <Route element={<ExecutiveManagementDashboardLayout />}>
                <Route path="/executive-management-dashboard" element={<ExecutiveDashboardPage />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute roles={ACCESS.maintenance} />}>
              <Route element={<UserRoleManagementLayout />}>
                <Route path="/maintenance-tickets-diagnostics" element={<MaintenanceTicketsPage />} />
              </Route>
            </Route>
            <Route element={<GuestCheckoutSessionLayout />}>
              <Route path="/guest-checkout-session" element={<GuestCheckoutSessionPage />} />
              <Route path="/c/:chargerId/:connectorIndex" element={<GuestCheckoutSessionPage />} />
            </Route>
            <Route element={<StationLocatorAvailabilityLayout />}>
              <Route path="/" element={<StationLocatorPage />} />
              <Route path="/station-locator-availability" element={<StationLocatorPage />} />
            </Route>
            <Route element={<AnonymousWebLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/401" element={<UnauthorizedPage />} />
              <Route path="/403" element={<ForbiddenPage />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="/500" element={<ServerErrorPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
